import { FileUploader } from "@/lib/FileUpload/FileUploader";
import { MediaCollection, MediaFieldsFragment, ModelType } from "@/gql/graphql";
import { useEffect, useState } from "react";
import { useGqlMutation } from "@/lib/GraphQLCodegen/fetcher";
import {
  createMediaMutation,
  deleteMediaMutation,
  mediaSetNewOrderMutation,
} from "@/app/Media/GraphQL/mediaMutations";
import { VehicleRecord } from "@/app/Vehicles/GraphQL/vehicleRecordQuery";
import { useTranslations } from "use-intl";
import { SortableList } from "@/lib/Components/Sortable/SortableList";
import {
  SortableItem,
  SortableItemDragHandle,
} from "@/lib/Components/Sortable/SortableItem";
import { Button } from "@/components/catalyst/button";
import { ImoovaImage } from "@/app/Media/Components/Image";
import { useQueryClient } from "@tanstack/react-query";
import { TrashIcon } from "@heroicons/react/24/outline";

export function SortableVehicleImages({ record }: { record: VehicleRecord }) {
  const t = useTranslations("vehicle.type");
  const [items, setItems] = useState(record.images);
  const queryClient = useQueryClient();

  const { mutateAsync: createMedia } = useGqlMutation(createMediaMutation, {
    onSuccess: () => {},
  });
  const { mutateAsync: setNewOrder } = useGqlMutation(
    mediaSetNewOrderMutation,
    {
      onSuccess: () => {
        queryClient.invalidateQueries({
          predicate: (query) => {
            return (query.queryKey.at(0) as string)?.includes("Vehicle");
          },
        });
      },
    },
  );

  useEffect(() => {
    setNewOrder({
      ids: items.map((item) => item.id),
    });
  }, [items]);

  //Watch the size of the images array and reset the items if it changes
  useEffect(() => {
    setItems(record.images);
  }, [record.images.length, record.id]);

  return (
    <div className="space-y-6">
      {record.images.length > 0 ? (
        <div>
          <h2 className="mb-4 text-lg font-medium">Vehicle Images</h2>
          <div className="[&_ul]:!grid [&_ul]:!grid-cols-1 [&_ul]:!gap-6 [&_ul]:sm:!grid-cols-2 [&_ul]:lg:!grid-cols-3">
            <SortableList
              items={items}
              onChange={setItems}
              renderItem={(item) => {
                return <SortableVehicleImageCard key={item.id} media={item} />;
              }}
            />
          </div>
        </div>
      ) : (
        <div className="py-8 text-center">
          <div className="mx-auto max-w-sm">
            <img
              src={record.heroImage}
              alt="default image"
              className="w-full rounded-lg shadow-sm"
            />
            <div className="mt-4 text-sm text-gray-500">
              This is the default image for a {t(record.type)}.
            </div>
          </div>
        </div>
      )}

      <div className="space-y-4 pt-10">
        <div>
          <h3 className="text-lg font-medium">Upload Vehicle Images</h3>
          <p className="text-sm text-gray-600">
            Upload up to 10 images for this vehicle. Supported formats: JPEG,
            PNG, WebP, AVIF
          </p>
        </div>
        <FileUploader
          maxFileCount={10}
          className="rounded-lg border-2 border-dashed border-gray-300 p-8 transition-colors hover:border-gray-400"
          onUpload={async (uploads) => {
            const res = uploads.map(async (upload) => {
              return createMedia({
                input: {
                  file_name: upload.file.name,
                  objectId: record.id,
                  objectType: ModelType.Vehicle,
                  collectionName: MediaCollection.Images,
                  key: upload.filePath,
                },
              });
            });

            await Promise.allSettled(res);
            await queryClient.invalidateQueries();
          }}
        />
      </div>
    </div>
  );
}

function SortableVehicleImageCard({ media }: { media: MediaFieldsFragment }) {
  return (
    <SortableItem id={media.id}>
      <div className="group/media group relative aspect-[4/3] w-full overflow-hidden rounded-lg bg-gray-100 shadow-sm transition-all duration-200 hover:shadow-lg">
        <MediaPreview media={media} />
        <SortableItemDragHandle>
          <div className="absolute inset-0 z-10 cursor-grab active:cursor-grabbing" />
        </SortableItemDragHandle>
      </div>
    </SortableItem>
  );
}

function MediaPreview({ media }: { media: MediaFieldsFragment }) {
  const queryClient = useQueryClient();
  const { mutateAsync: deleteMedia, isPending } = useGqlMutation(
    deleteMediaMutation,
    {
      onSuccess: () => {
        return queryClient.invalidateQueries({
          predicate: (query) => {
            return (query.queryKey.at(0) as string)?.includes("Vehicle");
          },
        });
      },
    },
  );

  return (
    <div className="relative h-full w-full">
      <ImoovaImage
        media={media}
        alt={media.name}
        className="h-full w-full object-cover"
      />
      <div className="absolute left-3 top-3 z-20 opacity-0 transition-opacity duration-200 group-hover/media:opacity-100">
        <Button
          color="red"
          type="button"
          isLoading={isPending}
          onClick={async (e) => {
            e.stopPropagation();
            await deleteMedia({
              id: media.id,
            });
          }}
          LeadingIcon={TrashIcon}
        />
      </div>
    </div>
  );
}
